import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Repeat, Plus, Minus, Trash2, Pause, Play } from 'lucide-react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@mlm/ui';
import {
  createAutoship,
  deleteAutoship,
  getMe,
  getStorefront,
  listMyAutoship,
  updateAutoship,
  type StorefrontProduct,
} from '../lib/api';
import { useMySlots, usePricingSlotId } from '../lib/hooks/use-my-slots';
import { useAutoshipCartStore } from '../lib/autoship-cart-store';
import { fmtCurrency } from '../lib/format';

const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

export function AutoshipPage() {
  const qc = useQueryClient();
  // `activeSlotId` binds the SUBSCRIPTION (attribution) and is validated against
  // owned active slots; `pricingSlotId` reads the persisted store so the price
  // query doesn't fire slotless during hydration. They converge once slots load,
  // and the create button is disabled until `activeSlotId` resolves.
  const { activeSlotId } = useMySlots();
  const pricingSlotId = usePricingSlotId();
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  const storefrontQ = useQuery({
    queryKey: ['storefront', pricingSlotId],
    queryFn: () => getStorefront(undefined, undefined, pricingSlotId),
  });
  const subsQ = useQuery({ queryKey: ['autoship'], queryFn: listMyAutoship });

  const cart = useAutoshipCartStore((s) => s.items);
  const addItem = useAutoshipCartStore((s) => s.add);
  const setQty = useAutoshipCartStore((s) => s.setQty);
  const clearCart = useAutoshipCartStore((s) => s.clear);

  const [dayOfMonth, setDayOfMonth] = useState(10);
  const [currency, setCurrency] = useState<'cash' | 'gc' | 'coin'>('cash');
  const [formError, setFormError] = useState<string | null>(null);

  const products = storefrontQ.data?.products ?? [];
  const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const cartEntries = Object.entries(cart).filter(([, q]) => q > 0);

  const create = useMutation({
    mutationFn: async () => {
      if (!activeSlotId) throw new Error('no_active_slot');
      const items = cartEntries.map(([productId, qty]) => ({ productId, qty }));
      if (items.length === 0) throw new Error('no_items');
      return createAutoship({
        slotId: activeSlotId,
        currency,
        dayOfMonth,
        fulfillmentMode: 'ship',
        shipToAddress: (meQ.data?.address as Record<string, unknown> | null) ?? null,
        items,
      });
    },
    onSuccess: () => {
      clearCart();
      setFormError(null);
      qc.invalidateQueries({ queryKey: ['autoship'] });
    },
    onError: (err: unknown) => {
      const code =
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
        (err as Error).message;
      const friendly: Record<string, string> = {
        no_items: 'Add at least one product first.',
        slot_not_active: 'Your slot is not active.',
        no_active_slot: 'You need an active slot.',
      };
      setFormError(friendly[code] ?? 'Could not create the auto-ship.');
    },
  });

  const toggle = useMutation({
    mutationFn: (s: { id: string; status: 'active' | 'paused' }) =>
      updateAutoship(s.id, { status: s.status === 'active' ? 'paused' : 'active' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['autoship'] }),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => deleteAutoship(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['autoship'] }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-h1">
          <Repeat className="h-6 w-6 text-muted-foreground" /> Auto-Ship
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick products and a day each month — we'll place the order automatically and pay it from
          your wallet.
        </p>
      </div>

      {/* Build a new subscription */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">New auto-ship</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Chosen items */}
          {cartEntries.length === 0 ? (
            <p className="text-sm text-muted-foreground">No products added yet. Pick some below.</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {cartEntries.map(([productId, qty]) => {
                const p = byId.get(productId);
                return (
                  <li
                    key={productId}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <span className="truncate">{p?.name ?? productId}</span>
                    <span className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setQty(productId, qty - 1)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-8 text-center tabular-nums">{qty}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setQty(productId, qty + 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setQty(productId, 0)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Run on day</label>
              <select
                className="h-10 rounded-md border bg-background px-3 text-sm"
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Number(e.target.value))}
              >
                {DAYS.map((d) => (
                  <option key={d} value={d}>{`Day ${d}`}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Pay from</label>
              <select
                className="h-10 rounded-md border bg-background px-3 text-sm"
                value={currency}
                onChange={(e) => setCurrency(e.target.value as 'cash' | 'gc' | 'coin')}
              >
                <option value="cash">Cash wallet</option>
                <option value="gc">Gift certificates</option>
                <option value="coin">Coins</option>
              </select>
            </div>
            <Button
              disabled={cartEntries.length === 0 || create.isPending || !activeSlotId}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Creating…' : 'Create auto-ship'}
            </Button>
          </div>
          {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
        </CardContent>
      </Card>

      {/* Product picker */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add products</CardTitle>
        </CardHeader>
        <CardContent>
          {storefrontQ.isLoading ? (
            <Skeleton className="h-40" />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {products
                .filter((p: StorefrontProduct) => !p.isKit)
                .slice(0, 24)
                .map((p) => (
                  <div key={p.id} className="rounded-lg border p-3">
                    <div className="truncate text-sm font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground tabular-nums">
                      {fmtCurrency(p.priceEffective)}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 w-full"
                      onClick={() => addItem(p.id)}
                    >
                      <Plus className="h-3.5 w-3.5" /> Add
                    </Button>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Existing subscriptions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your auto-ships</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {subsQ.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : !subsQ.data?.length ? (
            <p className="p-6 text-sm text-muted-foreground">No auto-ships yet.</p>
          ) : (
            <ul className="divide-y">
              {subsQ.data.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-3 px-6 py-3 text-sm"
                >
                  <div>
                    <div className="font-medium">
                      {s.items.length} item{s.items.length === 1 ? '' : 's'} · day {s.dayOfMonth} ·{' '}
                      {s.currency}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Next run {s.nextRunDate}
                      {s.runs[0] ? ` · last: ${s.runs[0].status} (${s.runs[0].runMonth})` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={s.status === 'active' ? 'success' : 'muted'}>{s.status}</Badge>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      title={s.status === 'active' ? 'Pause' : 'Resume'}
                      onClick={() => toggle.mutate({ id: s.id, status: s.status })}
                    >
                      {s.status === 'active' ? (
                        <Pause className="h-3.5 w-3.5" />
                      ) : (
                        <Play className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      title="Cancel"
                      onClick={() => cancel.mutate(s.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
